import React, { useState } from "react";

import {
  Box,
  Button,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";

import * as yup from "yup";
import { Formik } from "formik";

import useAuthentication from "hooks/useAuthentication";
import { useTheme } from 'contexts/ThemeContext';
import { AuthPageLayout } from 'components/common/AuthPageLayout';

function ResetPassword() {
  const token = window.location.pathname.split("/reset-password/")[1];
  const { setPassword } = useAuthentication();
  const { theme } = useTheme();
  const [submitError, setSubmitError] = useState<string | null>(null);

  return (
    <AuthPageLayout pageTitle="Set Password">
      <Formik
        initialValues={{ newPassword: '', confirmPassword: '' }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (values.newPassword && values.confirmPassword && token) {
              await setPassword(token, values.newPassword);
              setSubmitError(null);
            } else {
              console.log('Missing newPassword, confirmPassword, or token');
            }
          } catch (error) {
            console.error('Error setting password:', error);
            setSubmitError('Failed to set password. Please try again.');
          } finally {
            setSubmitting(false);
          }
        }}
        validationSchema={yup.object({
          newPassword: yup
            .string()
            .min(6, 'Password must be at least 6 characters')
            .required('New Password Required'),
          confirmPassword: yup
            .string()
            .oneOf([yup.ref('newPassword')], 'Passwords must match')
            .required('Confirm Password Required'),
        })}
      >
        {({ getFieldProps, errors, handleSubmit, touched }) => (
          <form onSubmit={handleSubmit}>
            <Box display='flex' flexDirection='column'>
              <FormControl variant='standard' sx={{ mb: 2 }}>
                <TextField
                  type='password'
                  variant='outlined'
                  label='New Password'
                  {...getFieldProps('newPassword')}
                  id='newPassword'
                  error={touched.newPassword && !!errors.newPassword}
                  helperText={touched.newPassword && errors.newPassword}
                  sx={{
                    backgroundColor: '#f9f9f9',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ccc',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.customer.secondaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.customer.primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.customer.primaryColor,
                    },
                  }}
                />
              </FormControl>
              <FormControl variant='standard' sx={{ mt: 2 }}>
                <TextField
                  type='password'
                  variant='outlined'
                  label='Confirm Password'
                  {...getFieldProps('confirmPassword')}
                  id='confirmPassword'
                  error={touched.confirmPassword && !!errors.confirmPassword}
                  helperText={
                    touched.confirmPassword && errors.confirmPassword
                  }
                  sx={{
                    backgroundColor: '#f9f9f9',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ccc',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.customer.secondaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.customer.primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.customer.primaryColor,
                    },
                  }}
                />
              </FormControl>

              {submitError && (
                <Typography color='error' sx={{ mt: 2, textAlign: 'center' }}>
                  {submitError}
                </Typography>
              )}

              <Box display='flex' justifyContent='center' sx={{ mt: 2 }}>
                <Button
                  variant='contained'
                  size='large'
                  type='submit'
                  sx={{
                    backgroundColor: theme.customer.primaryColor,
                    borderRadius: `${theme.customer.borderRadius || 8}px`,
                    fontFamily: theme.customer.fontFamily || 'inherit',
                    '&:hover': {
                      backgroundColor: theme.customer.primaryColor,
                      opacity: 0.9,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${theme.customer.primaryColor}40`,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Set Password
                </Button>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
    </AuthPageLayout>
  );
}

export default ResetPassword;
